import { listOrgLearningSessions } from "./sessionHelpers"
import { listOrgLearningEvents } from "./learningEventHelpers"
import { getRangeBounds, toMillis, isWithin, percentChange } from "./dateRangeHelpers"

const DAY_MS = 86400000
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const HOUR_BUCKETS = [0, 3, 6, 9, 12, 15, 18, 21]

const bucketLabel = (hour) => {
  const bucketStart = HOUR_BUCKETS?.filter((h) => h <= hour)?.pop() ?? 0
  return `${String(bucketStart).padStart(2, "0")}h`
}

// dayIndex: JS getDay() is 0=Sun..6=Sat — remap to Mon-first to match DAY_LABELS
const dayLabelFor = (date) => {
  const jsDay = date.getDay()
  const monFirstIndex = jsDay === 0 ? 6 : jsDay - 1
  return DAY_LABELS?.[monFirstIndex]
}

// ---- Engagement Overview: DAL/WAL are always fixed windows; the rest
// (sessions, hours, avg duration, returning, lessons) respect the selected range.
export const computeEngagementOverview = async (orgId, rangeKey = "30d") => {
  const [{ sessions }, { events }] = await Promise.all([
    listOrgLearningSessions(orgId),
    listOrgLearningEvents(orgId),
  ])

  const now = Date.now()
  const dailyIds = new Set()
  const weeklyIds = new Set()
  sessions?.forEach((s) => {
    const ms = toMillis(s?.startedAt)
    if (ms === null) return
    if (now - ms <= DAY_MS) dailyIds.add(s?.userId)
    if (now - ms <= 7 * DAY_MS) weeklyIds.add(s?.userId)
  })

  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey)

  const currentSessions = sessions?.filter((s) => isWithin(s?.startedAt, start, end)) || []
  const prevSessions = sessions?.filter((s) => isWithin(s?.startedAt, prevStart, prevEnd)) || []

  const totalSeconds = currentSessions?.reduce((sum, s) => sum + (s?.durationSeconds || 0), 0)
  const hours = Math.round((totalSeconds / 3600) * 10) / 10
  const avgSessionMinutes = currentSessions?.length ? Math.round(totalSeconds / currentSessions.length / 60) : 0

  const currentIds = new Set(currentSessions?.map((s) => s?.userId))
  const prevIds = new Set(prevSessions?.map((s) => s?.userId))
  const returningLearners = Array.from(currentIds)?.filter((id) => prevIds.has(id))?.length

  const lessonsCompleted =
    events?.filter((e) => e?.type === "lesson_completed" && isWithin(e?.createdAt, start, end))?.length || 0

  const prevLessonsCompleted =
    events?.filter((e) => e?.type === "lesson_completed" && isWithin(e?.createdAt, prevStart, prevEnd))?.length || 0

  return {
    dailyActiveLearners: dailyIds.size,
    weeklyActiveLearners: weeklyIds.size,
    sessions: { value: currentSessions?.length, change: percentChange(currentSessions?.length, prevSessions?.length) },
    hours: { value: hours, change: percentChange(hours, Math.round(((prevSessions?.reduce((s, x) => s + (x?.durationSeconds || 0), 0)) / 3600) * 10) / 10) },
    avgSessionMinutes: { value: avgSessionMinutes },
    returningLearners: { value: returningLearners },
    lessonsCompleted: { value: lessonsCompleted, change: percentChange(lessonsCompleted, prevLessonsCompleted) },
  }
}

// ---- Engagement Heatmap: real day-of-week x hour-of-day session density,
// plus the stat/metric rows the card's header needs.
export const computeEngagementHeatmap = async (orgId, rangeKey = "30d") => {
  const { sessions } = await listOrgLearningSessions(orgId)
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey)

  const currentSessions = sessions?.filter((s) => isWithin(s?.startedAt, start, end)) || []
  const prevSessions = sessions?.filter((s) => isWithin(s?.startedAt, prevStart, prevEnd)) || []

  const grid = {}
  DAY_LABELS?.forEach((day) => {
    grid[day] = {}
    HOUR_BUCKETS?.forEach((h) => (grid[day][`${String(h).padStart(2, "0")}h`] = 0))
  })

  currentSessions?.forEach((s) => {
    const ms = toMillis(s?.startedAt)
    if (ms === null) return
    const date = new Date(ms)
    const day = dayLabelFor(date)
    const hourKey = bucketLabel(date.getHours())
    grid[day][hourKey] += 1
  })

  const heatmapData = DAY_LABELS?.map((day) => ({
    key: day,
    data: HOUR_BUCKETS?.map((h) => ({ key: `${String(h).padStart(2, "0")}h`, data: grid?.[day]?.[`${String(h).padStart(2, "0")}h`] || 0 })),
  }))

  // Find the peak hour-bucket across the whole grid
  let peakHour = "—"
  let peakCount = -1
  DAY_LABELS?.forEach((day) => {
    HOUR_BUCKETS?.forEach((h) => {
      const key = `${String(h).padStart(2, "0")}h`
      if (grid?.[day]?.[key] > peakCount) {
        peakCount = grid?.[day]?.[key]
        peakHour = key
      }
    })
  })

  const avgSessionMinutes = currentSessions?.length
    ? Math.round(currentSessions?.reduce((sum, s) => sum + (s?.durationSeconds || 0), 0) / currentSessions.length / 60)
    : 0

  // Drop-off: sessions under 60 seconds — a bounce, not real engagement
  const dropoffCount = currentSessions?.filter((s) => (s?.durationSeconds || 0) < 60)?.length || 0
  const dropoffRate = currentSessions?.length ? Math.round((dropoffCount / currentSessions.length) * 1000) / 10 : 0
  const prevDropoffCount = prevSessions?.filter((s) => (s?.durationSeconds || 0) < 60)?.length || 0
  const prevDropoffRate = prevSessions?.length ? Math.round((prevDropoffCount / prevSessions.length) * 1000) / 10 : 0

  const statItems = [
    {
      title: "Active Sessions",
      count: currentSessions?.length || 0,
      comparisonText: `Compared to ${prevSessions?.length || 0} last period`,
      percentageChange: `${Math.abs(percentChange(currentSessions?.length, prevSessions?.length) || 0)}%`,
      trend: (percentChange(currentSessions?.length, prevSessions?.length) || 0) >= 0 ? "up" : "down",
    },
    {
      title: "Total Sessions (all-time)",
      count: sessions?.length || 0,
      comparisonText: `${currentSessions?.length || 0} in the selected period`,
      percentageChange: `${Math.abs(percentChange(currentSessions?.length, prevSessions?.length) || 0)}%`,
      trend: (percentChange(currentSessions?.length, prevSessions?.length) || 0) >= 0 ? "up" : "down",
    },
  ]

    const metricItems = [
    {
      id: "avg-session",
      label: "Average Session Length",
      value: `${avgSessionMinutes} min`,
      trendDirection: "up",
      trendColor: "#10b981",
      trendStrokeColor: "#10b981",
    },
    {
      id: "peak-hour",
      label: "Peak Activity Hour",
      value: peakHour,
      trendDirection: "up",
      trendColor: "#6b7280",
      trendStrokeColor: "#6b7280",
    },
    {
      id: "dropoff",
      label: "Session Drop-off Rate",
      value: `${dropoffRate}%`,
      trendDirection: dropoffRate > prevDropoffRate ? "down" : "up",
      trendColor: dropoffRate > prevDropoffRate ? "#E84045" : "#40E5D1",
      trendStrokeColor: dropoffRate > prevDropoffRate ? "#F08083" : "#40E5D1",
    },
  ]

  return { heatmapData, statItems, metricItems, hasSessions: (sessions?.length || 0) > 0 }
}